(function () {
  const WIDGET_SELECTOR = '[data-bandsintown-widget]';
  const SCRIPT_SELECTOR = 'script[data-bandsintown-script]';
  const SCRIPT_SRC = 'https://widgetv3.bandsintown.com/main.min.js';
  const EVENT_SELECTOR = '.bit-event';
  const TICKETS_BUTTON_SELECTOR = '.bit-button-type-tickets';
  const SHOW_MORE_SELECTOR = '[data-tour-show-more]';
  const PMC_ALIGNMENT_DELAYS = [0, 150, 500, 1000, 2000];
  const SHOW_MORE_DELAYS = [0, 150, 500, 1000, 2000, 4000];

  let widgetScriptPromise;

  function getTourSection(widget) {
    return widget.closest('.nl-tour-dates');
  }

  function updatePmcAlignment(widget) {
    if (!(widget instanceof HTMLElement)) {
      return false;
    }

    const section = getTourSection(widget);
    const pmc = section?.querySelector('.nl-tour-dates__pmc');
    const bandsintownLink = section?.querySelector('.nl-tour-dates__bandsintown-link');
    const footerTarget = pmc || bandsintownLink;

    if (!section || !footerTarget || window.innerWidth <= 749) {
      section?.style.removeProperty('--nl-tour-pmc-inset-start');
      section?.style.removeProperty('--nl-tour-pmc-inset-end');
      return false;
    }

    const eventRow = widget.querySelector(EVENT_SELECTOR);
    const ticketsButton = widget.querySelector(TICKETS_BUTTON_SELECTOR);

    if (!eventRow) {
      section.style.removeProperty('--nl-tour-pmc-inset-start');
      section.style.removeProperty('--nl-tour-pmc-inset-end');
      return false;
    }

    const footerRect = footerTarget.getBoundingClientRect();
    const eventRect = eventRow.getBoundingClientRect();
    const startInset = Math.max(0, Math.round(eventRect.left - footerRect.left));

    section.style.setProperty('--nl-tour-pmc-inset-start', `${startInset}px`);

    if (!ticketsButton) {
      section.style.removeProperty('--nl-tour-pmc-inset-end');
      return false;
    }

    const ticketsRect = ticketsButton.getBoundingClientRect();
    const endInset = Math.max(0, Math.round(footerRect.right - ticketsRect.right));
    section.style.setProperty('--nl-tour-pmc-inset-end', `${endInset}px`);
    return true;
  }

  function clearPmcAlignmentTimers(widget) {
    widget._pmcAlignmentTimeouts?.forEach(window.clearTimeout);
    widget._pmcAlignmentTimeouts = [];
  }

  function queuePmcAlignment(widget) {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    clearPmcAlignmentTimers(widget);

    widget._pmcAlignmentTimeouts = PMC_ALIGNMENT_DELAYS.map(function (delay) {
      return window.setTimeout(function () {
        if (updatePmcAlignment(widget)) {
          clearPmcAlignmentTimers(widget);
        }
      }, delay);
    });
  }

  function updateShowMoreLabel(button, isExpanded) {
    const label = button.querySelector('[data-tour-show-more-label]');

    if (label) {
      label.textContent = isExpanded ? 'SHOW LESS' : 'SHOW MORE';
    }

    button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  function setupShowMore(section) {
    const button = section?.querySelector(SHOW_MORE_SELECTOR);

    if (!section || !button || button.dataset.bound === 'true') {
      return;
    }

    button.dataset.bound = 'true';
    button.addEventListener('click', function () {
      const isExpanded = !section.classList.contains('is-expanded');
      section.classList.toggle('is-expanded', isExpanded);
      updateShowMoreLabel(button, isExpanded);
    });
  }

  function updateMobileShowMore(widget) {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    const section = getTourSection(widget);
    const button = section?.querySelector(SHOW_MORE_SELECTOR);

    if (!section || !button) {
      return;
    }

    setupShowMore(section);

    const shouldShow = window.innerWidth <= 749 && widget.querySelectorAll(EVENT_SELECTOR).length > 4;
    button.hidden = !shouldShow;
    section.classList.toggle('nl-tour-dates--can-expand', shouldShow);

    if (!shouldShow) {
      section.classList.remove('is-expanded');
      updateShowMoreLabel(button, false);
    }
  }

  function clearShowMoreTimers(widget) {
    widget._showMoreTimeouts?.forEach(window.clearTimeout);
    widget._showMoreTimeouts = [];
  }

  function queueMobileShowMore(widget) {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    clearShowMoreTimers(widget);

    widget._showMoreTimeouts = SHOW_MORE_DELAYS.map(function (delay) {
      return window.setTimeout(function () {
        updateMobileShowMore(widget);
      }, delay);
    });
  }

  function getWidgetScript() {
    if (window.BIT?.widgetInitializer?.append) {
      return Promise.resolve(window.BIT.widgetInitializer);
    }

    if (widgetScriptPromise) {
      return widgetScriptPromise;
    }

    widgetScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(SCRIPT_SELECTOR);

      if (existingScript) {
        if (existingScript.dataset.loaded === 'true' && window.BIT?.widgetInitializer) {
          resolve(window.BIT.widgetInitializer);
          return;
        }

        existingScript.addEventListener(
          'load',
          function () {
            resolve(window.BIT?.widgetInitializer);
          },
          { once: true }
        );
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.charset = 'utf-8';
      script.dataset.bandsintownScript = 'true';
      script.addEventListener(
        'load',
        function () {
          script.dataset.loaded = 'true';
          resolve(window.BIT?.widgetInitializer);
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return widgetScriptPromise;
  }

  function setRenderedState(widget) {
    widget.dataset.bandsintownState = 'ready';
    const loading = widget.querySelector('[data-bandsintown-loading]');
    const error = widget.querySelector('[data-bandsintown-error]');

    if (loading) {
      loading.hidden = true;
    }

    if (error) {
      error.hidden = true;
    }

    queueMobileShowMore(widget);
    queuePmcAlignment(widget);
  }

  function setErrorState(widget) {
    widget.dataset.bandsintownState = 'error';
    const loading = widget.querySelector('[data-bandsintown-loading]');
    const error = widget.querySelector('[data-bandsintown-error]');

    if (loading) {
      loading.hidden = true;
    }

    if (error) {
      error.hidden = false;
    }
  }

  function observeRender(widget) {
    if (widget.dataset.bandsintownObserved === 'true') {
      return;
    }

    widget.dataset.bandsintownObserved = 'true';

    const finishIfRendered = function () {
      if (widget.querySelector('.bit-widget')) {
        setRenderedState(widget);
        return true;
      }

      return false;
    };

    if (finishIfRendered()) {
      return;
    }

    const observer = new MutationObserver(function () {
      if (finishIfRendered()) {
        observer.disconnect();
      }
    });

    observer.observe(widget, { childList: true, subtree: true });

    window.setTimeout(function () {
      observer.disconnect();

      if (widget.dataset.bandsintownState !== 'ready') {
        setErrorState(widget);
      }
    }, 10000);
  }

  function initializeWidget(widget) {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    const anchor = widget.querySelector('.bit-widget-initializer');

    if (!anchor) {
      return;
    }

    if (widget.querySelector('.bit-widget')) {
      setRenderedState(widget);
      return;
    }

    widget.dataset.bandsintownState = 'loading';
    observeRender(widget);

    getWidgetScript()
      .then(function (widgetInitializer) {
        if (widget.querySelector('.bit-widget')) {
          setRenderedState(widget);
          return;
        }

        if (!widgetInitializer?.append) {
          setErrorState(widget);
          return;
        }

        widgetInitializer.append(anchor);
      })
      .catch(function () {
        setErrorState(widget);
      });
  }

  function initializeWidgets(root) {
    const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
    const widgets = scope.matches?.(WIDGET_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(WIDGET_SELECTOR));

    widgets.forEach(initializeWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeWidgets(document);
    });
  } else {
    initializeWidgets(document);
  }

  window.addEventListener('resize', function () {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(function (widget) {
      updatePmcAlignment(widget);
      queueMobileShowMore(widget);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initializeWidgets(event.target);
  });
})();
